import React, { useMemo } from 'react';

import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { EventArg } from '@react-navigation/native';

import BlackjackIcon from '@/assets/icons/blackjack';
import { CheckmarkOutlinedIcon } from '@/assets/icons/tab/checkmark-outlined';
import { LobbyIcon } from '@/assets/icons/tab/home';
import { useSbkBetImport } from '@/feature/bet-share-sbk/hooks/use-sbk-bet-import';
import { BetsScreen } from '@/feature/bets-sbk/screens/BetsScreen';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { useAppInit } from '@/hooks/use-app-init';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, ProductConfigKeys, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';
import { FloatingModalProvider } from '@/utils/floatingModal/FloatingModalProvider';

import { AccountTabConfig, PromosTabConfig, TabBar, TabOptions, mapTabsFromTabSettings } from '../tabs';
import { LobbyStack } from './lobbyStack';
import { SweepsTabNavigatorParamList } from './types';

const TabSweeps = createBottomTabNavigator<SweepsTabNavigatorParamList>();

const SweepsTabsConfig = [
    { name: 'SweepsLobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    {
        name: 'SweepsEntries',
        component: BetsScreen,
        icon: CheckmarkOutlinedIcon,
        title: 'Track',
    },
] as const;

export const SweepsTabsNavigator = () => {
    useOneLinkNavigation();
    useSbkBetImport(Product.Sweeps);

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);
    const showCasinoSwitchTab = useShouldShowCasinoSwitchTab();

    // Maps over an array of tabs and updates the title of each tab based on the 'Tab Bar Settings'
    const tabsConfig = useMemo(() => {
        return mapTabsFromTabSettings(
            [
                ...SweepsTabsConfig,
                showCasinoSwitchTab ? null : PromosTabConfig,
                showCasinoSwitchTab ? BlackjackSwitchTab : null,
                AccountTabConfig,
            ].filter(Boolean),
            tabSettings
        );
    }, [tabSettings, showCasinoSwitchTab]);

    useAppInit();

    return (
        <FloatingModalProvider>
            <TabSweeps.Navigator tabBar={TabBar} screenOptions={TabOptions}>
                {tabsConfig.map(it => (
                    <TabSweeps.Screen key={it.name} {...it} options={{ tabBarIcon: it.icon, title: it.title }} />
                ))}
            </TabSweeps.Navigator>
        </FloatingModalProvider>
    );
};

const EmptyScreen = () => null;

/**
 * Reads the Blackjack quick-launch game ID from Prismic.
 * If the game ID isn't configured, the user lands on the Casino lobby instead.
 */
const BLACKJACK_GAME_ID_KEY = 'blackjackGameId';
const getBlackjackGameId = (): string | undefined =>
    useJurisdictionStore.getState().jurisdictionSettings?.productConfig?.keyValuePairs?.[
        ProductConfigKeys.ContentKeys
    ]?.[BLACKJACK_GAME_ID_KEY] || undefined;

const BlackjackSwitchTab = {
    name: 'SwitchToCasino',
    component: EmptyScreen,
    icon: () => <BlackjackIcon />,
    title: 'Blackjack',
    listeners: () => ({
        tabPress: (e: EventArg<'tabPress', true>) => {
            // Prevent default navigation to the tab's screen
            e.preventDefault();
            const blackjackGameId = getBlackjackGameId();
            useProductTransitionStore
                .getState()
                .actions.triggerTransition(
                    Product.Casino,
                    blackjackGameId ? { name: 'CasinoGameWebView', params: { gameId: blackjackGameId } } : undefined
                );
        },
    }),
} as const;

/**
 * Determines if we should render the Casino switch tab on Sweeps.
 * Only shows when Casino product is available in the jurisdiction.
 */
const useShouldShowCasinoSwitchTab = () => {
    const { isProductAvailable } = useAvailableProducts();
    const isCasinoAvailable = isProductAvailable(Product.Casino);
    return isCasinoAvailable;
};
